import { JSX, useContext, useMemo } from 'react';

import { Label } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { LocalizationContext } from 'localization';
import { IGoodHistoryItem } from 'schemas';
import { EGoodsAmountOperating } from 'types';

import s from './AmountReportModal.module.scss';

export const useColumns = (): Column<IGoodHistoryItem>[] => {
  const { t } = useContext(LocalizationContext);

  const getLabelOperating = (
    operating: EGoodsAmountOperating
  ): JSX.Element | null => {
    switch (operating) {
      case EGoodsAmountOperating.ADD:
        return <Label color="blue" text={t('warehouse.operation.add')} />;

      case EGoodsAmountOperating.REFUND:
        return <Label color="red" text={t('log.payment.refund')} />;
      case EGoodsAmountOperating.CREATE:
        return (
          <Label
            color="gray-secondary"
            text={t('goods.amount.report.table.create')}
          />
        );
      case EGoodsAmountOperating.DISPOSAL:
        return (
          <Label color="orange" text={t('warehouse.operation.disposal')} />
        );
      case EGoodsAmountOperating.SOLD:
        return <Label color="green" text={t('log.payment.created')} />;
      case EGoodsAmountOperating.SET:
        return (
          <Label
            color="gray-secondary"
            text={t('goods.amount.report.table.change')}
          />
        );
      default:
        return null;
    }
  };

  return useMemo(
    () => [
      {
        Header: t('date'),
        accessor: 'created_at',
        Cell: ({ value }: { value: string }) => {
          const date = dayjs(value);
          return (
            <div className={s.date}>
              <span>
                {date.year() === dayjs().year()
                  ? dayjs(value).format('DD MMM')
                  : `${dayjs(value).format('DD MMM YY')} г.`}
              </span>
              <span className={s.time}>{date.format('HH:mm:ss')}</span>
            </div>
          );
        },
        width: 190,
      },
      {
        Header: t('bonuses.type'),
        accessor: 'operation',
        Cell: ({ value }: { value: EGoodsAmountOperating }) => {
          return getLabelOperating(value);
        },
        width: 150,
      },
      {
        Header: t('amount'),
        accessor: 'delta',
        Cell: ({
          value,
          row,
        }: {
          value: EGoodsAmountOperating;
          row: Row<IGoodHistoryItem>;
        }) => {
          const operation = row?.original?.operation;
          if (operation === EGoodsAmountOperating.CREATE) return '—';
          if (operation === EGoodsAmountOperating.SET) return '—';
          if (operation === EGoodsAmountOperating.ADD) {
            return (
              <span>
                + {value} {t('pcs')}
              </span>
            );
          } else {
            return (
              <span>
                - {value} {t('pcs')}
              </span>
            );
          }
        },
        width: 150,
      },
      {
        Header: t('host.seance.rest'),
        accessor: 'quantity_after',
        headerTooltip: {
          content: t('goods.amount.report.tooltip'),
          contentClassName: s.tooltip,
        },
        Cell: ({ value }: { value: string }) => (
          <span>
            {value} {t('pcs')}
          </span>
        ),
        width: 150,
      },
      {
        Header: t('comment'),
        accessor: 'comment',
        Cell: ({ value }: { value: string }) => <span>{value}</span>,
        width: 150,
      },
      {
        Header: t('worker'),
        accessor: 'initiator',
        Cell: ({ value }: { value: IGoodHistoryItem['initiator'] }) => (
          <span>
            {value?.first_name} {value?.last_name}
          </span>
        ),
        width: 150,
      },
    ],
    [t]
  );
};
